import React, {useContext, useState} from 'react';
import {Input} from '../../dimijs/src/lib/components/Input';
import {Button} from '../../dimijs/src/lib/components/Button';
import {useUserSource} from '../../sources/useUserSource';
import axios from 'axios';
import {IBlockProps} from './Blocks';
import styles from './Login.module.scss';
import classnames from 'classnames';
import {DataCtx} from '../../dimijs/src/lib/utils/dataContext';

const auth = async (username, password) => {
  try {
    await axios.get('/authenticate', {auth: {username, password}});
    return true;
  } catch (e) {
    return false;
  }
};

export interface IAdditionalLoginBlockProps {
  loginTitle: string;
  signupTitle: string;
  defaultSlide?: string;
}
export interface ILoginBlockProps
  extends IBlockProps,
    IAdditionalLoginBlockProps {}

export const Login = (props: ILoginBlockProps) => {
  const [slide, setSlide] = useState(props.defaultSlide || 'login');
  const [login, setLogin] = useState('');
  const [pass, setPass] = useState('');
  const [email, setEmail] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const userSource = useUserSource();
  const {initProps} = useContext(DataCtx);

  const handleGoogleLogin = () => {
      const popup = window.open(
          'https://www.pushapp.fit/auth/google',
          '_blank',
          'width=500,height=600'
      );
      const interval = setInterval(() => {
          if (popup?.closed) {
              clearInterval(interval);
              window.location.reload();
          }
      }, 500);
  };

  const handleAppleLogin = () => {
      const popup = window.open(
          'https://www.pushapp.fit/auth/apple',
          '_blank',
          'width=500,height=700'
      );
      const interval = setInterval(() => {
          if (popup?.closed) {
              clearInterval(interval);
              window.location.reload();
          }
      }, 500);
  };

  const handleFacebookLogin = () => {
      const popup = window.open(
          'https://www.pushapp.fit/auth/facebook',
          '_blank',
          'width=500,height=600'
      );
      const interval = setInterval(() => {
          if (popup?.closed) {
              clearInterval(interval);
              window.location.reload();
          }
      }, 500);
  };

  const handleSignup = async () => {
    setError('');
    setSuccess('');

    if (!email || !pass) {
      setError('Email and password are required');
      return;
    }

    if (pass !== confirmPass) {
      setError('Passwords do not match');
      return;
    }

    if (pass.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      const response = await axios.post('/api/auth/signup', {
        email,
        password: pass,
        login: login || email.split('@')[0]
      });

      if (response.data.success) {
        setSuccess('Account created! Please check your email to verify your account.');
        setEmail('');
        setPass('');
        setConfirmPass('');
        setLogin('');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Signup failed. Please try again.');
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setSuccess('');

    if (!email) {
      setError('Email is required');
      return;
    }

    try {
      const response = await axios.post('/api/auth/forgot-password', {email});

      if (response.data.success) {
        setSuccess('If an account with that email exists, a password reset link has been sent.');
        setEmail('');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to send reset email. Please try again.');
    }
  };

  if (initProps.user)
    return null;
  return (
    <div className={classnames(styles.slider, 'relative')}>
      <div
        className={classnames(
          styles.flexSlides,
          slide === 'signup' && styles.moveToSignup,
          slide === 'forgot' && styles.moveToForgot,
        )}
        style={{
          display: 'flex',
          transition: 'transform 0.3s ease',
          transform: slide === 'signup' ? 'translateX(-100%)' : slide === 'forgot' ? 'translateX(-200%)' : 'translateX(0)'
        }}>
        {/* Login Slide */}
        <div className={classnames(styles.slide)} style={{minWidth: '100%'}}>
          <h3>{props.loginTitle || 'Login'}</h3>
          {error && <div style={{color: 'red', marginBottom: '10px'}}>{error}</div>}
          {success && <div style={{color: 'green', marginBottom: '10px'}}>{success}</div>}
          <form onSubmit={(e) => e.preventDefault()}>
            <Input value={login} onChange={setLogin} label={'Login or Email'} />
            <Input value={pass} onChange={setPass} label={'Password'} type='password'/>
            <Button
              caption="Login"
              type={'submit'}
              color={'primary'}
              onClick={() => {
                setError('');
                setSuccess('');
                auth(login, pass).then(result => {
                  if (result) window.location.reload();
                  else setError('Invalid login or password');
                });
              }}
            />
            <div style={{margin: '10px 0', textAlign: 'center'}}>OR</div>
            <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
              <Button caption='Sign in with Google' onClick={handleGoogleLogin}/>
              <Button caption='Sign in with Apple' onClick={handleAppleLogin}/>
              <Button caption='Sign in with Facebook' onClick={handleFacebookLogin}/>
            </div>
          </form>
          <p>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setError('');
                setSuccess('');
                setSlide('forgot');
              }}>
              Forgot password?
            </a>
          </p>
          <p>
            Don't have an account?{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setError('');
                setSuccess('');
                setSlide('signup');
              }}>
              Register
            </a>
          </p>
        </div>

        {/* Signup Slide */}
        <div className={classnames(styles.slide)} style={{minWidth: '100%'}}>
          <h3>{props.signupTitle || 'Sign Up'}</h3>
          {error && <div style={{color: 'red', marginBottom: '10px'}}>{error}</div>}
          {success && <div style={{color: 'green', marginBottom: '10px'}}>{success}</div>}
          <form onSubmit={(e) => e.preventDefault()}>
            <Input value={email} onChange={setEmail} label={'Email'} type='email' />
            <Input value={login} onChange={setLogin} label={'Username (optional)'} />
            <Input value={pass} onChange={setPass} label={'Password'} type='password' />
            <Input value={confirmPass} onChange={setConfirmPass} label={'Confirm Password'} type='password' />
            <Button
              caption="Sign Up"
              color={'primary'}
              onClick={handleSignup}
            />
            <div style={{margin: '10px 0', textAlign: 'center'}}>OR</div>
            <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
              <Button caption='Sign up with Google' onClick={handleGoogleLogin}/>
              <Button caption='Sign up with Apple' onClick={handleAppleLogin}/>
              <Button caption='Sign up with Facebook' onClick={handleFacebookLogin}/>
            </div>
          </form>
          <p>
            Already have an account?{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setError('');
                setSuccess('');
                setSlide('login');
              }}>
              Login
            </a>
          </p>
        </div>

        {/* Forgot Password Slide */}
        <div className={classnames(styles.slide)} style={{minWidth: '100%'}}>
          <h3>Forgot Password</h3>
          {error && <div style={{color: 'red', marginBottom: '10px'}}>{error}</div>}
          {success && <div style={{color: 'green', marginBottom: '10px'}}>{success}</div>}
          <form onSubmit={(e) => e.preventDefault()}>
            <Input value={email} onChange={setEmail} label={'Email'} type='email' />
            <Button
              caption="Send Reset Link"
              color={'primary'}
              onClick={handleForgotPassword}
            />
          </form>
          <p>
            Remember your password?{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setError('');
                setSuccess('');
                setSlide('login');
              }}>
              Login
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
